// ── CLIENT-SIDE IMAGE COMPRESSION ────────────────────────────────────────────
// Shrinks a user-picked image BEFORE it ever leaves the browser. Two reasons
// this happens here rather than server-side:
//   • a 4MB phone photo would blow Firestore's 1MB document limit outright
//   • uploading full-resolution files wastes the user's mobile data for pixels
//     that get thrown away anyway
//
// Quality note: the on-screen avatar is small, so 256px is already ~2x a 128px
// display slot and stays crisp on retina. Gallery photos (a later phase) use a
// much larger cap for the same reason in reverse — those ARE viewed large.

import { AVATAR_MAX_PX, AVATAR_QUALITY, AVATAR_MAX_BYTES } from '../constants/designer';
import {
  PHOTO_FULL_MAX_PX, PHOTO_FULL_QUALITY, PHOTO_FULL_MAX_BYTES,
  PHOTO_THUMB_MAX_PX, PHOTO_THUMB_QUALITY, FIRESTORE_DOC_SAFE_CHARS,
  PHOTO_TARGET_BYTES,
} from '../constants/gallery';
import {
  SHARE_THUMB_MAX_PX, SHARE_THUMB_QUALITY, SHARE_THUMB_MAX_CHARS,
} from '../constants/share';

// Reads a File into an HTMLImageElement, resolving only once decoded so callers
// can rely on naturalWidth/Height being populated.
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode-failed')); };
    img.src = url;
  });
}

/**
 * Decodes a file for the CROP EDITOR and keeps its object URL alive.
 *
 * `loadImage` above revokes the URL the instant it decodes, which is right for
 * a one-shot compress — the pixels are already in the element. The editor,
 * though, mounts a SECOND <img> pointing at the same src, and against a revoked
 * URL that one silently loads nothing and reports a width of 0.
 *
 * The caller owns the returned `revoke` and must call it when the editor
 * closes, or the blob stays in memory for the life of the tab.
 */
export function loadImageForEditing(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, src: url, revoke: () => URL.revokeObjectURL(url) });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode-failed')); };
    img.src = url;
  });
}

/**
 * Encodes a SQUARE REGION of an already-decoded image as a small JPEG data URL.
 *
 * The region is given in source pixels — `sx`, `sy`, `side` — which is what
 * both callers naturally have: the plain path computes a centre crop, and the
 * crop editor converts its pan/zoom back into the same three numbers. Keeping
 * the encoding here means both go through one quality ladder and one byte cap.
 */
export function encodeAvatarRegion(img, sx, sy, side, size = AVATAR_MAX_PX) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  // Better downscaling than the default — without it, a large photo reduced in
  // one step aliases badly on fine detail like hair and text.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);

  // JPEG, not PNG: a photographic avatar as PNG is roughly 10x larger for no
  // visible gain. Step the quality down if an unusually detailed image still
  // lands over budget, rather than rejecting the user's photo outright.
  let quality = AVATAR_QUALITY;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);
  while (dataUrlBytes(dataUrl) > AVATAR_MAX_BYTES && quality > 0.4) {
    quality -= 0.12;
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }
  if (dataUrlBytes(dataUrl) > AVATAR_MAX_BYTES) throw new Error('too-large');

  return dataUrl;
}

// Centre-crops to a square and scales down. Square because the avatar is
// rendered in a circle everywhere — cropping here means the stored bytes match
// what's displayed, instead of paying to store corners that CSS hides.
//
// Still used wherever a picture is taken as-is, with no crop step.
export async function compressAvatar(file, size = AVATAR_MAX_PX) {
  if (!file || !/^image\//.test(file.type)) throw new Error('not-an-image');
  const img = await loadImage(file);
  const side = Math.min(img.naturalWidth, img.naturalHeight);
  return encodeAvatarRegion(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, size);
}

// ── GALLERY PHOTOS ───────────────────────────────────────────────────────────
// Two renditions per photo, because they're read in very different ways:
//
//   thumb — shown in the gallery grid, stored ON the photo's metadata document.
//           A grid of 24 photos must stay cheap to list, so this is small.
//   full  — shown when a photo is opened, stored in a SEPARATE document that is
//           only fetched on demand. Keeping it out of the metadata doc is what
//           stops "list the gallery" from downloading megabytes nobody looked at.
//
// Aspect ratio is preserved for both (unlike the avatar, which is square-cropped
// to match its circular frame) — cropping someone's portfolio work would be
// destructive.
// WebP encoding support, probed once and cached.
//
// This check is NOT optional. `canvas.toDataURL('image/webp')` does not throw on
// a browser that can't encode WebP — it silently returns a PNG instead. A PNG of
// a photograph is several times LARGER than the JPEG it replaced, so skipping
// this check would make files bigger on exactly the browsers least able to cope
// (Safari before 14). Reading the prefix back is the only reliable test.
let _webpSupport = null;
function supportsWebP() {
  if (_webpSupport !== null) return _webpSupport;
  try {
    const c = document.createElement('canvas');
    c.width = c.height = 1;
    _webpSupport = c.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    _webpSupport = false;
  }
  return _webpSupport;
}

export async function compressPhoto(file, opts = {}) {
  const {
    fullMaxPx = PHOTO_FULL_MAX_PX,
    thumbMaxPx = PHOTO_THUMB_MAX_PX,
    fullQuality = PHOTO_FULL_QUALITY,
    thumbQuality = PHOTO_THUMB_QUALITY,
    fullMaxBytes = PHOTO_FULL_MAX_BYTES,
    targetBytes = PHOTO_TARGET_BYTES,
  } = opts;

  if (!file || !/^image\//.test(file.type)) throw new Error('not-an-image');
  const img = await loadImage(file);

  const mime = supportsWebP() ? 'image/webp' : 'image/jpeg';

  const render = (maxPx, quality) => {
    const scale = Math.min(1, maxPx / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    // Without high-quality smoothing a 12MP phone photo reduced in one step
    // aliases badly on fine detail — tile grout, fabric weave, text.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);
    return { dataUrl: canvas.toDataURL(mime, quality), w, h };
  };

  // Hard ceiling: the decoded budget AND the stored base64 length. Firestore
  // counts the stored string, which is ~4/3 the decoded size, so a
  // decoded-only check understates the document by a third.
  const overHardLimit = (u) =>
    dataUrlBytes(u) > fullMaxBytes || u.length > FIRESTORE_DOC_SAFE_CHARS;

  // Reaching the target is a LADDER, and the rung order is what preserves
  // quality. Measured on real photo-like input, quality-first hit q≈0.44 before
  // resolution was touched at all — visibly mushy at 2048px. A sharp 1500px
  // image and a smeared 2048px one cost the same bytes; the sharp one looks
  // dramatically better. So:
  //
  //   1. ease quality down only to a floor that still looks good (0.72)
  //   2. then shed resolution, which removes bytes without adding artefacts
  //   3. only then dip quality further, for images that resist both
  const SAFE_Q_FLOOR = 0.72;
  const MIN_PX = 1200;

  let px = fullMaxPx;
  let q = fullQuality;
  let full = render(px, q);
  const over = () => dataUrlBytes(full.dataUrl) > targetBytes;

  while (over() && q > SAFE_Q_FLOOR) {
    q = Math.max(SAFE_Q_FLOOR, q - 0.05);
    full = render(px, q);
  }
  while (over() && px > MIN_PX) {
    px = Math.round(px * 0.85);
    full = render(px, q);
  }
  while (over() && q > 0.5) {
    q -= 0.07;
    full = render(px, q);
  }

  // Overshooting the target is acceptable; breaching the hard limit is not.
  while (overHardLimit(full.dataUrl) && (q > 0.4 || px > 900)) {
    if (q > 0.4) q -= 0.08; else px = Math.round(px * 0.8);
    full = render(px, q);
  }
  if (overHardLimit(full.dataUrl)) throw new Error('too-large');

  const thumb = render(thumbMaxPx, thumbQuality);
  return {
    fullUrl: full.dataUrl,
    thumbUrl: thumb.dataUrl,
    width: full.w,
    height: full.h,
    bytes: dataUrlBytes(full.dataUrl),
    mimeType: mime,
  };
}

/**
 * A picture for a chat message.
 *
 * Reuses compressPhoto rather than encoding its own: that routine already
 * produces exactly the two renditions a message needs, and it already knows
 * the awkward parts — the quality-before-resolution ladder that keeps a
 * downsized photo sharp, WebP detection, and the base64 ceiling that a
 * decoded-size check understates by a third. A second copy here would be the
 * same code with different constants, and would go stale.
 *
 * `thumbUrl` becomes the message's inline preview and `fullUrl` the separate
 * media document — see chatService.sendMessage for why they are split.
 */
export async function compressChatImage(file, budget) {
  const out = await compressPhoto(file, {
    fullMaxPx: budget.fullMaxPx,
    fullQuality: budget.fullQuality,
    fullMaxBytes: budget.fullMaxBytes,
    targetBytes: budget.fullTargetBytes,
    thumbMaxPx: budget.previewMaxPx,
    thumbQuality: budget.previewQuality,
  });
  // The PREVIEW's dimensions, because that is what the bubble lays out.
  // `Math.min(1, …)` matters: a picture already smaller than the preview
  // budget is not enlarged to meet it — without the clamp a 500px image would
  // be recorded as 800px, and the bubble would reserve a box the picture never
  // fills.
  const s = Math.min(1, budget.previewMaxPx / Math.max(out.width, out.height));
  return {
    previewUrl: out.thumbUrl,
    fullUrl: out.fullUrl,
    width: Math.max(1, Math.round(out.width * s)),
    height: Math.max(1, Math.round(out.height * s)),
    fullWidth: out.width,
    fullHeight: out.height,
    previewBytes: dataUrlBytes(out.thumbUrl),
    fullBytes: out.bytes,
  };
}

/**
 * The little picture on a share card.
 *
 * Its input is a data URL that is ALREADY a thumbnail — a gallery tile's
 * `thumbUrl`, encoded at 640px for a three-across grid. Sending that as-is
 * would put 30-60KB on a message that renders it at about 72 square, and every
 * reader of the thread would pay for it on every load. This re-encodes to the
 * size the card actually draws.
 *
 * Returns '' rather than throwing when anything goes wrong — a card with no
 * picture is still a card, and a share that failed because a thumbnail would
 * not decode is not. See chatService.normalizeShare, which drops an oversized
 * one for the same reason.
 */
/**
 * A gallery thumbnail re-derived from an image that is ALREADY STORED.
 *
 * Needed for exactly one thing: promoting a post's second picture to cover
 * when the first is removed. The metadata document carries the cover's
 * thumbnail and nothing else, so a new cover needs a new thumbnail, and the
 * only copy of that picture is the full rendition in its blob document.
 *
 * ASPECT-PRESERVING, unlike compressShareThumb, which centre-crops to a square
 * for the fixed tile on a chat card. A gallery thumbnail has to match what the
 * uploader produced or the grid would show one item cropped differently from
 * every other.
 *
 * Returns null rather than throwing: a failure here must leave the cover where
 * it is, not lose the post.
 */
export async function thumbFromDataUrl(
  src,
  maxPx = PHOTO_THUMB_MAX_PX,
  quality = PHOTO_THUMB_QUALITY,
) {
  if (!src || typeof src !== 'string') return null;
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode-failed'));
      el.src = src;
    });
    if (!img.naturalWidth || !img.naturalHeight) return null;

    const scale = Math.min(1, maxPx / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);

    const mime = supportsWebP() ? 'image/webp' : 'image/jpeg';
    return { thumbUrl: canvas.toDataURL(mime, quality), width: w, height: h };
  } catch {
    return null;
  }
}

export async function compressShareThumb(src, maxPx = SHARE_THUMB_MAX_PX, quality = SHARE_THUMB_QUALITY) {
  if (!src || typeof src !== 'string') return '';
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode-failed'));
      el.src = src;
    });

    // SQUARE, CENTRE-CROPPED, to match the card's fixed tile. Letterboxing a
    // panorama into that box would spend most of the bytes on empty space.
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    if (!side) return '';
    const size = Math.min(maxPx, side);
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      img,
      (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side,
      0, 0, size, size,
    );

    // WebP where it exists — the same probe compressPhoto uses, and for the
    // same reason: a browser that cannot encode it returns a PNG instead, which
    // for a photograph is several times larger than the JPEG it replaced.
    const mime = supportsWebP() ? 'image/webp' : 'image/jpeg';
    let q = quality;
    let out = canvas.toDataURL(mime, q);
    while (out.length > SHARE_THUMB_MAX_CHARS && q > 0.35) {
      q -= 0.1;
      out = canvas.toDataURL(mime, q);
    }
    return out.length <= SHARE_THUMB_MAX_CHARS ? out : '';
  } catch {
    return '';
  }
}

// Byte length of a data URL's payload. base64 encodes 3 bytes as 4 chars, and
// trailing '=' padding represents no actual bytes.
export function dataUrlBytes(dataUrl) {
  const i = String(dataUrl).indexOf(',');
  if (i < 0) return 0;
  const b64 = dataUrl.slice(i + 1);
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}
